import React, { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import styles from './toast.module.scss';

export type Props = {
  message: ReactNode;
  isVisible: boolean;
  duration?: number;
  onClose?: () => void;
};

const fadeOutTiming = 500; //in ms

function Toast({ message, isVisible, duration = 4000, onClose }: Props) {
  const [isFading, setIsFading] = useState(false);
  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);

    if (isVisible) {
      setIsFading(false);

      fadeTimeoutRef.current = setTimeout(() => {
        setIsFading(true);
      }, duration);

      closeTimeoutRef.current = setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, duration + fadeOutTiming);
    }

    return () => {
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, [isVisible, duration, onClose, message]);

  if (!isVisible) return null;

  return (
    <div
      className={`
        ${styles.toast} 
        ${isFading ? styles.fadeOut : styles.fadeIn}
      `}
      style={{ '--fade-out-timing': `${fadeOutTiming}ms` } as CSSProperties}
    >
      <div className={styles.toastContent}>
        <span className={styles.toastMessage}>{message}</span>
      </div>
    </div>
  );
}

export default Toast;
